import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db6e15nqhq9pk5mecsu0';
const SUBJECTS = ['内容合作', '内容反馈', '版权相关', '其他建议'];
const MESSAGE_LIMIT = 500;

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMessageLength(0);
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
        return;
      }

      setStatus('success');
      setMessageLength(0);
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请检查网络后重试。');
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-foreground-600">称呼</span>
          <input
            type="text"
            name="name"
            required
            placeholder="如何称呼您"
            className="h-10 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-foreground-600">邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="用于接收回复的邮箱"
            className="h-10 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </label>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-foreground-600">主题</span>
        <select
          name="subject"
          defaultValue={SUBJECTS[0]}
          className="h-10 cursor-pointer rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        >
          {SUBJECTS.map((subject) => (
            <option key={subject} value={subject}>
              {subject}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="flex items-center justify-between text-xs font-semibold text-foreground-600">
          留言内容
          <span className={messageLength > MESSAGE_LIMIT ? 'text-accent-600' : 'text-foreground-400'}>
            {messageLength}/{MESSAGE_LIMIT}
          </span>
        </span>
        <textarea
          name="message"
          required
          rows={5}
          maxLength={MESSAGE_LIMIT}
          onChange={(e) => setMessageLength(e.target.value.length)}
          placeholder="请描述您的合作意向或反馈建议（500 字以内）"
          className="resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm leading-relaxed text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-100"
        />
      </label>

      <input
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="contact-field-guard"
      />

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === 'submitting' ? (
          <>
            <i className="ri-loader-4-line animate-spin text-base" />
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            提交留言
          </>
        )}
      </button>

      {status === 'success' && (
        <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2 text-sm font-medium text-secondary-900">
          <i className="ri-checkbox-circle-line text-base" />
          提交成功！感谢您的留言，我们会尽快处理并回复。
        </p>
      )}
      {status === 'error' && (
        <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2 text-sm font-medium text-accent-900">
          <i className="ri-error-warning-line text-base" />
          {formError}
        </p>
      )}
      <p className="text-xs text-foreground-400">
        说明：本留言表单用于内容合作与反馈收集，提交后我们会通过邮箱与您联系。
      </p>
    </form>
  );
}